import { useTexture } from "@react-three/drei";

/**
 * Modern commuter train carriage — table seat + window for shoulder surfing.
 * Waypoints:
 *   wp_train_establish — aisle view
 *   wp_train_seat — Max's table laptop (~[0, 1.0, 0.3])
 *   wp_train_passenger — look toward seat behind
 */
export function TrainCarriage() {
  return (
    <group>
      {/* floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[4.2, 14]} />
        <meshStandardMaterial color="#3a4048" roughness={0.9} />
      </mesh>
      {/* ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 2.35, 0]}>
        <planeGeometry args={[4.2, 14]} />
        <meshStandardMaterial color="#303a44" roughness={0.92} />
      </mesh>
      {/* side walls */}
      <mesh position={[-2.05, 1.15, 0]} receiveShadow>
        <boxGeometry args={[0.12, 2.3, 14]} />
        <meshStandardMaterial color="#26343f" roughness={0.85} />
      </mesh>
      <mesh position={[2.05, 1.15, 0]} receiveShadow>
        <boxGeometry args={[0.12, 2.3, 14]} />
        <meshStandardMaterial color="#26343f" roughness={0.85} />
      </mesh>

      <Windows />
      <PhotographicCarriageEnd />
      <SeatRow z={-0.35} withTable />
      <SeatRow z={-1.9} />
      <SeatRow z={-3.45} />
      <SeatRow z={1.25} />
      <Passenger x={-0.85} z={-1.75} coat="#253d59" hair="#33251f" />
      <Passenger x={0.85} z={-1.75} coat="#39495a" hair="#1d1714" flip />
      <Passenger x={-0.85} z={-3.3} coat="#594437" hair="#5b3928" />
      <Passenger x={0.85} z={-3.3} coat="#2f4e48" hair="#2b201a" flip />
      <Passenger x={-0.85} z={1.38} coat="#4d3554" hair="#432d23" flip />
      <Passenger x={0.85} z={1.38} coat="#34445c" hair="#1e1b18" />
      <LuggageRack />
      {/* brighter carriage — avoid reading as a black void */}
      <hemisphereLight args={["#e8eef4", "#5a6068", 0.55]} />
      <pointLight position={[0, 2.1, 0.4]} intensity={1.35} distance={8} color="#f5f8fc" />
      <pointLight position={[0, 2.1, -1.6]} intensity={0.95} distance={7} color="#eef2f8" />
      <pointLight position={[0, 2.1, 2.2]} intensity={0.7} distance={6} color="#e8eef4" />
      <directionalLight position={[0, 2, 3]} intensity={0.4} color="#d0e0f0" />
    </group>
  );
}

function PhotographicCarriageEnd() {
  const texture = useTexture("/cdn/sim/remote-work/env-refs/scene_carriage.png");
  return (
    <mesh position={[0, 1.35, -3]}>
      <planeGeometry args={[5.2, 3.47]} />
      <meshBasicMaterial map={texture} toneMapped={false} />
    </mesh>
  );
}

function Windows() {
  return (
    <group>
      {[-2.4, -0.4, 1.6].map((z, i) => (
        <group key={i}>
          <mesh position={[-1.98, 1.42, z]} rotation={[0, Math.PI / 2, 0]}>
            <planeGeometry args={[1.75, 0.92]} />
            <meshStandardMaterial
              color="#79a5b6"
              emissive="#5f8f9d"
              emissiveIntensity={0.65}
              roughness={0.2}
              metalness={0.3}
            />
          </mesh>
          <mesh position={[1.98, 1.42, z]} rotation={[0, -Math.PI / 2, 0]}>
            <planeGeometry args={[1.75, 0.92]} />
            <meshStandardMaterial
              color="#79a5b6"
              emissive="#5f8f9d"
              emissiveIntensity={0.65}
              roughness={0.2}
              metalness={0.3}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function SeatRow({ z, withTable }: { z: number; withTable?: boolean }) {
  return (
    <group position={[0, 0, z]}>
      <Seat x={-0.85} />
      <Seat x={0.85} flip />
      {withTable && (
        <mesh position={[0, 0.78, 0]} castShadow receiveShadow>
          <boxGeometry args={[0.7, 0.04, 0.45]} />
          <meshStandardMaterial color="#5a6570" roughness={0.55} metalness={0.25} />
        </mesh>
      )}
      {withTable && (
        <group position={[0.15, 0.82, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.36, 0.015, 0.24]} />
            <meshStandardMaterial color="#222830" metalness={0.4} roughness={0.4} />
          </mesh>
          <mesh position={[0, 0.12, -0.1]} rotation={[-0.4, 0, 0]}>
            <boxGeometry args={[0.36, 0.22, 0.01]} />
            <meshStandardMaterial color="#0c1016" />
          </mesh>
        </group>
      )}
    </group>
  );
}

function Seat({ x, flip }: { x: number; flip?: boolean }) {
  const s = flip ? -1 : 1;
  return (
    <group position={[x, 0, 0]} rotation={[0, flip ? Math.PI : 0, 0]}>
      <mesh position={[0, 0.42, 0]} castShadow>
        <boxGeometry args={[0.55, 0.12, 0.5]} />
        <meshStandardMaterial color="#2a4a6e" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.72, -0.18 * s]} castShadow>
        <boxGeometry args={[0.55, 0.55, 0.1]} />
        <meshStandardMaterial color="#234060" roughness={0.9} />
      </mesh>
    </group>
  );
}

function Passenger({
  x,
  z,
  coat,
  hair,
  flip,
}: {
  x: number;
  z: number;
  coat: string;
  hair: string;
  flip?: boolean;
}) {
  return (
    <group position={[x, 0.58, z]} rotation={[0, flip ? Math.PI : 0, 0]}>
      <mesh position={[0, 0.64, 0]} castShadow>
        <sphereGeometry args={[0.14, 20, 20]} />
        <meshStandardMaterial color="#c99170" roughness={0.82} />
      </mesh>
      <mesh position={[0, 0.71, -0.015]} castShadow>
        <sphereGeometry args={[0.145, 18, 14, 0, Math.PI * 2, 0, Math.PI * 0.58]} />
        <meshStandardMaterial color={hair} roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.25, 0]} castShadow>
        <capsuleGeometry args={[0.2, 0.42, 8, 16]} />
        <meshStandardMaterial color={coat} roughness={0.88} />
      </mesh>
      <mesh position={[-0.16, -0.17, 0.02]} rotation={[0.12, 0, -0.08]} castShadow>
        <capsuleGeometry args={[0.055, 0.34, 6, 10]} />
        <meshStandardMaterial color="#29313a" roughness={0.9} />
      </mesh>
      <mesh position={[0.16, -0.17, 0.02]} rotation={[0.12, 0, 0.08]} castShadow>
        <capsuleGeometry args={[0.055, 0.34, 6, 10]} />
        <meshStandardMaterial color="#29313a" roughness={0.9} />
      </mesh>
    </group>
  );
}

function LuggageRack() {
  return (
    <mesh position={[0, 2.05, 0]}>
      <boxGeometry args={[3.6, 0.04, 12]} />
      <meshStandardMaterial color="#9aa3ad" metalness={0.5} roughness={0.4} transparent opacity={0.35} />
    </mesh>
  );
}
